import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Alert,
  Button,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  CircularProgress,
  Stack,
  Chip,
  TextField,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CardMembershipOutlinedIcon from "@mui/icons-material/CardMembershipOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import api from "../../api";
import InputField from "../../components/InputField";
import SubmitButton from "../../components/SubmitButton";
import { sanitizeStripeCheckoutUrl } from "../../utils/stripeCheckoutUrl";
import {
  formatPlanExpiryDate,
  formatPlanPrice,
  formatUkDateTime,
  planStateChipColor,
  planStateDateLabel,
  planStateLabel,
  statusChipColor,
  statusLabel,
} from "../../utils/mobileSubscriptionFormat";

const PLAY_STORE_URL =
  process.env.REACT_APP_PLAY_STORE_URL ||
  "https://play.google.com/store/apps/details?id=com.elevenplusmagic.app";
const APPLE_STORE_URL =
  process.env.REACT_APP_APPLE_STORE_URL ||
  "https://apps.apple.com/app/id6738291045";
const PRIVACY_URL =
  process.env.REACT_APP_PRIVACY_POLICY_URL ||
  "https://11plusmagic.co.uk/privacy-policy";
const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || "support@11plusmagic.co.uk";

const SettingRow = ({ icon, label, value, onClick, disabled = false }) => (
  <ListItemButton onClick={onClick} disabled={disabled} sx={{ py: 1.25, px: 1.5, borderRadius: 1 }}>
    <ListItemIcon sx={{ minWidth: 40, color: disabled ? "text.disabled" : "primary.main" }}>
      {icon}
    </ListItemIcon>
    <ListItemText
      primary={label}
      secondary={value || undefined}
      primaryTypographyProps={{ color: disabled ? "text.disabled" : "text.primary" }}
    />
    <ChevronRightIcon fontSize="small" sx={{ color: "text.secondary" }} />
  </ListItemButton>
);

const childNeedsPayment = (child) =>
  Boolean(child?.requires_payment) ||
  ["free", "trial_ended_pending"].includes(String(child?.subscription_status || ""));

const formatCoursePlanLabel = (row) => {
  const parts = [row?.course_name || "Course"];
  if (row?.plan_name) parts.push(row.plan_name);
  const where = [row?.mode === "in_person" ? "In person" : row?.mode === "online" ? "Online" : null, row?.location && row.location !== "online" ? row.location : null]
    .filter(Boolean)
    .join(" · ");
  return { title: parts.join(" · "), where };
};

/**
 * "Other" is not part of the admin-managed list on purpose.
 *
 * The reasons themselves come from Master Forms (`parent/mobile-subscriptions/cancellation-reasons`),
 * the same list the mobile app reads, so the two cancel screens cannot drift apart. The free-text
 * escape hatch is appended here by the client instead, so nobody can rename or delete it in the
 * admin UI and leave users with no way to say something the list does not cover.
 */
const OTHER_REASON = "Other";

const Setting = () => {
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [loadingPin, setLoadingPin] = useState(false);
  // Set by the student→parent switch while the emailed one-use PIN is still in place.
  const [mustChangePin, setMustChangePin] = useState(
    () => localStorage.getItem("mustChangeParentPin") === "1",
  );
  const [mobileChildren, setMobileChildren] = useState([]);
  const [subscriptionChildren, setSubscriptionChildren] = useState([]);
  const [plansAvailable, setPlansAvailable] = useState([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [subscriptionOpen, setSubscriptionOpen] = useState(false);
  const [subscriptionMode, setSubscriptionMode] = useState("manage"); // manage | history | cancel | payment
  const [selectedPlanByChild, setSelectedPlanByChild] = useState({});
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [checkoutBusyId, setCheckoutBusyId] = useState(null);
  const [portalBusyId, setPortalBusyId] = useState(null);
  const [unsubscribeBusyId, setUnsubscribeBusyId] = useState(null);
  const [resumeBusyId, setResumeBusyId] = useState(null);
  const [unsubscribeReasonByChild, setUnsubscribeReasonByChild] = useState({});
  // Which reason was picked, per child: a reason id as a string, or OTHER_REASON — the one value
  // that reveals the free-text box.
  const [unsubscribeChoiceByChild, setUnsubscribeChoiceByChild] = useState({});
  const [cancelReasons, setCancelReasons] = useState([]);
  const [paymentHistoryChildren, setPaymentHistoryChildren] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [paymentReturnMessage, setPaymentReturnMessage] = useState(null);
  const [paymentReturnText, setPaymentReturnText] = useState("");
  const fulfilledSessionRef = useRef(null);
  const deepLinkHandledRef = useRef(false);

  const hasMobileChild = mobileChildren.length > 0;

  const profileForm = useForm({
    defaultValues: { first_name: "", last_name: "" },
  });
  const passwordForm = useForm({
    defaultValues: {
      current_password: "",
      new_password: "",
      new_password_confirmation: "",
    },
  });
  const pinForm = useForm({
    defaultValues: {
      current_pin: "",
      new_pin: "",
      new_pin_confirmation: "",
    },
  });

  const loadMobileNames = useCallback(async () => {
    try {
      const res = await api.get("parent/students/names");
      const rows = Array.isArray(res.data?.data) ? res.data.data : [];
      setMobileChildren(rows.filter((r) => r.is_allow_mobile));
    } catch {
      setMobileChildren([]);
    }
  }, []);

  const loadSubscriptionOverview = useCallback(async () => {
    setLoadingSubscriptions(true);
    try {
      const res = await api.get("parent/mobile-subscriptions");
      const children = Array.isArray(res.data?.data?.children) ? res.data.data.children : [];
      const plans = Array.isArray(res.data?.data?.plans_available) ? res.data.data.plans_available : [];
      setSubscriptionChildren(children);
      setPlansAvailable(plans);
      setSelectedPlanByChild((prev) => {
        const next = { ...prev };
        children.forEach((child) => {
          const id = Number(child.student_user_id || child.user_id || child.mobile_user_id || 0);
          if (!id || next[id]) return;
          const currentPlanId = Number(child.effective_current_plan?.id || 0);
          const fallback = plans.find((p) => Number(p.price_pence || 0) > 0)?.id || plans[0]?.id;
          next[id] = currentPlanId || fallback || "";
        });
        return next;
      });
    } catch {
      setSubscriptionChildren([]);
      setPlansAvailable([]);
    } finally {
      setLoadingSubscriptions(false);
    }
  }, []);

  const loadPaymentHistory = useCallback(async () => {
    setLoadingHistory(true);
    setHistoryError("");
    try {
      const res = await api.get("parent/mobile-subscriptions/history");
      const children = Array.isArray(res.data?.data?.children) ? res.data.data.children : [];
      setPaymentHistoryChildren(children);
    } catch (err) {
      setPaymentHistoryChildren([]);
      setHistoryError(err.response?.data?.message || "Failed to load payment history.");
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadMobileNames();
      if (!cancelled) await loadSubscriptionOverview();
    })();
    return () => {
      cancelled = true;
    };
  }, [loadMobileNames, loadSubscriptionOverview]);

  // After Stripe Checkout / billing portal return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const mobileSub = params.get("mobile_sub");
    const canceled = params.get("mobile_sub_canceled");
    const portalReturn = params.get("mobile_portal_return");

    if (canceled === "1") {
      setPaymentReturnMessage("info");
      setPaymentReturnText("Payment was canceled. You can continue whenever you are ready.");
      toast.info("Payment was canceled.");
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }

    if (portalReturn === "1") {
      setPaymentReturnMessage("info");
      setPaymentReturnText("Checking your new card and charging any unpaid amount…");
      window.history.replaceState({}, "", window.location.pathname);
      api
        .post("parent/mobile-subscriptions/sync-billing")
        .then((res) => {
          const children = Array.isArray(res.data?.data?.children) ? res.data.data.children : [];
          const plans = Array.isArray(res.data?.data?.plans_available) ? res.data.data.plans_available : [];
          setSubscriptionChildren(children);
          setPlansAvailable(plans);
          setPaymentReturnMessage("success");
          setPaymentReturnText(
            res.data?.message || "Billing updated. Unpaid charges were retried with your new card."
          );
          toast.success("Billing updated.");
        })
        .catch(() => {
          setPaymentReturnMessage("warning");
          setPaymentReturnText(
            "Card may be saved, but we could not confirm the charge yet. Refresh in a moment."
          );
          void loadSubscriptionOverview();
        });
      return;
    }

    if (!sessionId || mobileSub !== "1") return;
    if (fulfilledSessionRef.current === sessionId) return;
    fulfilledSessionRef.current = sessionId;

    setPaymentReturnMessage("info");
    setPaymentReturnText("Verifying your subscription payment…");

    api
      .post("parent/mobile-subscriptions/fulfill", { session_id: sessionId })
      .then((res) => {
        setPaymentReturnMessage("success");
        setPaymentReturnText(res.data?.message || "Payment successful. Subscription is active.");
        toast.success(res.data?.message || "Subscription activated.");
        window.history.replaceState({}, "", window.location.pathname);
        return loadSubscriptionOverview();
      })
      .catch((err) => {
        const msg =
          err.response?.data?.message ||
          "Payment received, but we could not confirm the subscription yet. Refresh in a moment or contact support.";
        setPaymentReturnMessage("warning");
        setPaymentReturnText(msg);
        toast.warning(msg);
        window.history.replaceState({}, "", window.location.pathname);
        void loadSubscriptionOverview();
      });
  }, [loadSubscriptionOverview]);

  // Deep-link from student profile PIN switch (?open=manage|cancel|payment)
  useEffect(() => {
    if (deepLinkHandledRef.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("session_id") || params.get("mobile_sub") || params.get("mobile_portal_return") || params.get("mobile_sub_canceled")) {
      return;
    }
    const open = String(params.get("open") || "").toLowerCase();
    if (!["manage", "cancel", "payment", "history"].includes(open)) return;
    deepLinkHandledRef.current = true;
    window.history.replaceState({}, "", window.location.pathname);
    void openSubscriptionDialog(open);
    // openSubscriptionDialog is stable enough for this one-shot deep link
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mobileChildNames = useMemo(
    () => mobileChildren.map((c) => c.full_name).filter(Boolean).join(", "),
    [mobileChildren]
  );

  const displayChildren = useMemo(() => {
    if (subscriptionChildren.length > 0) return subscriptionChildren;
    return mobileChildren.map((c) => ({
      student_user_id: c.id,
      user_id: c.id,
      child_name: c.full_name,
      subscription_display_name: "Mobile",
      requires_payment: true,
      subscription_status: "free",
    }));
  }, [subscriptionChildren, mobileChildren]);

  const managePlanValue = useMemo(() => {
    if (!hasMobileChild) return "Mobile app only";
    if (loadingSubscriptions && subscriptionChildren.length === 0) return "Loading…";
    const mobileLabels = [
      ...new Set(displayChildren.map((c) => c.subscription_display_name).filter(Boolean)),
    ];
    const courseLabels = [
      ...new Set(
        displayChildren
          .flatMap((c) => (Array.isArray(c.course_plans) ? c.course_plans : []))
          .map((row) => row.course_name)
          .filter(Boolean)
      ),
    ];
    const parts = [`Mobile: ${mobileLabels.length ? mobileLabels.join(" · ") : "Mobile"}`];
    if (courseLabels.length) parts.push(`Course: ${courseLabels.join(" · ")}`);
    return parts.join(" · ");
  }, [hasMobileChild, loadingSubscriptions, subscriptionChildren.length, displayChildren]);

  const openSubscriptionDialog = async (mode) => {
    setSubscriptionMode(mode);
    if (mode === "history") {
      setSubscriptionOpen(true);
      void loadPaymentHistory();
      return;
    }

    setLoadingSubscriptions(true);
    try {
      const res = await api.get("parent/mobile-subscriptions");
      const children = Array.isArray(res.data?.data?.children) ? res.data.data.children : [];
      const plans = Array.isArray(res.data?.data?.plans_available) ? res.data.data.plans_available : [];
      setSubscriptionChildren(children);
      setPlansAvailable(plans);

      // Expired / insufficient card: go straight to Stripe Add payment method (no dialog list).
      if (mode === "payment") {
        const needsPayChild = children.find(
          (child) =>
            childNeedsPayment(child) &&
            !["apple", "google"].includes(String(child?.payment_provider || "").toLowerCase())
        );
        if (needsPayChild) {
          await openBillingPortal(needsPayChild);
          return;
        }
      }

      setSubscriptionOpen(true);
    } catch {
      setSubscriptionChildren([]);
      setPlansAvailable([]);
      setSubscriptionOpen(true);
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  // The admin-managed reason list, shared with the mobile app. Fetched the first time the cancel
  // view is shown — it is small, unchanging within a session, and not needed on the other tabs.
  // If it cannot be loaded the screen still works: "Other" alone is a complete way to cancel.
  useEffect(() => {
    if (!subscriptionOpen || subscriptionMode !== "cancel" || cancelReasons.length > 0) return undefined;
    let cancelled = false;
    api
      .get("parent/mobile-subscriptions/cancellation-reasons")
      .then((res) => {
        if (!cancelled) setCancelReasons(Array.isArray(res.data?.data) ? res.data.data : []);
      })
      .catch(() => {
        if (!cancelled) setCancelReasons([]);
      });
    return () => {
      cancelled = true;
    };
  }, [subscriptionOpen, subscriptionMode, cancelReasons.length]);

  const isExternalBilling = (child) =>
    ["apple", "google"].includes(String(child?.payment_provider || "").toLowerCase());

  /** Active Stripe subscription: swap the plan in place instead of opening a second checkout. */
  const changePlanInPlace = async (child, planIdOverride = null) => {
    const studentUserId = Number(child.student_user_id || child.user_id || child.mobile_user_id || 0);
    const planId = Number(planIdOverride || selectedPlanByChild[studentUserId] || 0);
    if (!studentUserId || !planId) {
      toast.error("Choose a plan first.");
      return;
    }

    // Changing plan turns auto-renew back on (the server resumes the Stripe subscription), so a
    // parent who had cancelled would be charged at renewal without having asked to be.
    if (child.unsubscribed_at) {
      const plan = plansAvailable.find((p) => Number(p.id) === planId);
      const price = plan ? `${formatPlanPrice(plan)}/${plan.billing_interval || "month"}` : "the new price";
      const when = child.current_period_end ? formatPlanExpiryDate(child.current_period_end) : "the next renewal";
      const ok = window.confirm(
        `Auto-renew is currently off. Changing to ${plan?.name || "this plan"} turns auto-renew back on, ` +
          `and ${price} will be charged on ${when}.\n\nContinue?`
      );
      if (!ok) return;
    }

    setCheckoutBusyId(studentUserId);
    try {
      const res = await api.post("parent/mobile-subscriptions/change-plan", {
        student_user_id: studentUserId,
        plan_id: planId,
      });
      toast.success(res.data?.message || "Plan updated.");
      await loadSubscriptionOverview();
    } catch (err) {
      const message =
        err.response?.data?.message || err.response?.data?.data?.error || "Could not change the plan.";
      // No live Stripe subscription to move — fall back to a fresh checkout.
      if (/no active stripe subscription/i.test(message)) {
        await startCheckout(child, planId);
        return;
      }
      toast.error(message);
    } finally {
      setCheckoutBusyId(null);
    }
  };

  const startCheckout = async (child, planIdOverride = null) => {
    const studentUserId = Number(child.student_user_id || child.user_id || child.mobile_user_id || 0);
    if (!studentUserId) {
      toast.error("Could not identify this child account.");
      return;
    }
    if (isExternalBilling(child)) {
      toast.info("This subscription is managed in the App Store or Google Play.");
      return;
    }

    const planId = Number(planIdOverride || selectedPlanByChild[studentUserId] || 0);

    setCheckoutBusyId(studentUserId);
    try {
      const payload = { student_user_id: studentUserId };
      if (planId > 0) payload.plan_id = planId;
      const res = await api.post("parent/mobile-subscriptions/checkout", payload);
      const url = sanitizeStripeCheckoutUrl(res.data?.data?.checkout_url);
      if (!url) {
        toast.error("Checkout URL was not returned. Please try again.");
        return;
      }
      setSubscriptionOpen(false);
      window.location.assign(url);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not start Stripe checkout.");
    } finally {
      setCheckoutBusyId(null);
    }
  };

  /**
   * What gets sent for a child: the chosen reason's own text, or what they typed under "Other".
   *
   * The text goes on being sent either way — it is what an admin reads, and what the API has always
   * required. `reasonId` rides along so the reasons stay countable even after one is renamed.
   * Empty text is what keeps the button disabled.
   */
  const resolvedCancelReason = (studentUserId) => {
    const choice = String(unsubscribeChoiceByChild[studentUserId] || "");
    if (!choice) return { text: "", reasonId: null };
    if (choice === OTHER_REASON) {
      return { text: String(unsubscribeReasonByChild[studentUserId] || "").trim(), reasonId: null };
    }
    const picked = cancelReasons.find((r) => String(r.id) === choice);
    return { text: picked?.name || "", reasonId: picked ? Number(picked.id) : null };
  };

  const cancelSubscription = async (child) => {
    const studentUserId = Number(child.student_user_id || child.user_id || child.mobile_user_id || 0);
    const subscriptionId = Number(child.subscription_id || 0);
    if (!studentUserId || !subscriptionId) {
      toast.error("Could not identify this subscription.");
      return;
    }
    if (!child.can_unsubscribe) {
      toast.info("This subscription cannot be cancelled from the web portal.");
      return;
    }

    const { text: reason, reasonId } = resolvedCancelReason(studentUserId);
    if (!unsubscribeChoiceByChild[studentUserId]) {
      toast.error("Please choose why you are cancelling.");
      return;
    }
    if (reason.length < 3) {
      toast.error("Please tell us why you are cancelling (at least 3 characters).");
      return;
    }

    setUnsubscribeBusyId(studentUserId);
    try {
      const res = await api.post("parent/mobile-subscriptions/unsubscribe", {
        student_user_id: studentUserId,
        subscription_id: subscriptionId,
        reason,
        // Null for "Other" — there is no master-form row behind free text.
        cancellation_reason_id: reasonId,
      });
      toast.success(res.data?.message || "Auto-renew turned off.");
      setUnsubscribeReasonByChild((prev) => ({ ...prev, [studentUserId]: "" }));
      setUnsubscribeChoiceByChild((prev) => ({ ...prev, [studentUserId]: "" }));
      await loadSubscriptionOverview();
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.data?.error || "Could not cancel subscription.");
    } finally {
      setUnsubscribeBusyId(null);
    }
  };

  /**
   * Turn auto-renew back on. Cancelling only ever set cancel_at_period_end in Stripe, so
   * while the paid window is still open this is a straight undo — no new checkout.
   */
  const resumeSubscription = async (child) => {
    const studentUserId = Number(child.student_user_id || child.user_id || child.mobile_user_id || 0);
    const subscriptionId = Number(child.subscription_id || 0);
    if (!studentUserId || !subscriptionId) {
      toast.error("Could not identify this subscription.");
      return;
    }

    setResumeBusyId(studentUserId);
    try {
      const res = await api.post("parent/mobile-subscriptions/resume", {
        student_user_id: studentUserId,
        subscription_id: subscriptionId,
      });
      toast.success(res.data?.message || "Auto-renew is back on.");
      await loadSubscriptionOverview();
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.data?.error ||
          "Could not turn auto-renew back on."
      );
    } finally {
      setResumeBusyId(null);
    }
  };

  const openBillingPortal = async (child) => {
    const studentUserId = Number(child.student_user_id || child.user_id || child.mobile_user_id || 0);
    if (!studentUserId) {
      toast.error("Could not identify this child account.");
      return;
    }
    if (isExternalBilling(child)) {
      toast.info("Update payment details in the App Store or Google Play.");
      return;
    }

    setPortalBusyId(studentUserId);
    try {
      const res = await api.post("parent/mobile-subscriptions/billing-portal", {
        student_user_id: studentUserId,
      });
      const url = String(res.data?.data?.portal_url || "").trim();
      if (!url || !/^https?:\/\//i.test(url)) {
        toast.error("Billing portal URL was not returned.");
        return;
      }
      setSubscriptionOpen(false);
      window.location.assign(url);
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          "Billing portal is not available yet. Subscribe first to manage payments and invoices."
      );
    } finally {
      setPortalBusyId(null);
    }
  };

  const onUpdateProfile = async (data) => {
    setLoadingProfile(true);
    try {
      const payload = {
        first_name: data.first_name,
        last_name: data.last_name,
      };
      await api.post("/update-profile", payload);
      toast.success("Profile updated successfully!");
    } catch (err) {
      toast.error("Failed to update profile");
    } finally {
      setLoadingProfile(false);
    }
  };

  const onChangePassword = async (data) => {
    setLoadingPassword(true);
    try {
      const payload = {
        current_password: data.current_password,
        new_password: data.new_password,
        new_password_confirmation: data.new_password_confirmation,
      };
      await api.post("parent/change-password", payload);
      toast.success("Password changed successfully!");
      localStorage.removeItem("mustChangePassword");
      const userDataStr = localStorage.getItem("userData");
      if (userDataStr) {
        try {
          const parsed = JSON.parse(userDataStr);
          parsed.must_change_password = false;
          localStorage.setItem("userData", JSON.stringify(parsed));
        } catch (e) {
          /* ignore */
        }
      }
      passwordForm.reset({
        current_password: "",
        new_password: "",
        new_password_confirmation: "",
      });
    } catch (err) {
      const backendErrors = err.response?.data?.errors;
      if (backendErrors) {
        Object.entries(backendErrors).forEach(([field, messages]) => {
          passwordForm.setError(field, {
            type: "server",
            message: messages[0],
          });
        });
      } else {
        toast.error("Failed to change password");
      }
    } finally {
      setLoadingPassword(false);
    }
  };

  const onChangePin = async (data) => {
    setLoadingPin(true);
    try {
      const payload = {
        current_pin: data.current_pin,
        new_pin: data.new_pin,
        new_pin_confirmation: data.new_pin_confirmation,
      };
      await api.post("parent/pin/change", payload);
      localStorage.removeItem("mustChangeParentPin");
      setMustChangePin(false);
      toast.success("PIN changed successfully!");
      pinForm.reset({
        current_pin: "",
        new_pin: "",
        new_pin_confirmation: "",
      });
    } catch (err) {
      const backendErrors = err.response?.data?.errors;
      if (backendErrors) {
        Object.entries(backendErrors).forEach(([field, messages]) => {
          pinForm.setError(field, {
            type: "server",
            message: messages[0],
          });
        });
      } else if (err.response?.status === 401) {
        toast.error(err.response?.data?.data?.error || "Current PIN is incorrect");
      } else {
        toast.error("Failed to change PIN");
      }
    } finally {
      setLoadingPin(false);
    }
  };

  const pinRules = {
    required: "PIN is required",
    minLength: { value: 4, message: "PIN must be 4 digits" },
    maxLength: { value: 4, message: "PIN must be 4 digits" },
    pattern: { value: /^[0-9]{4}$/, message: "PIN must contain only digits (0-9)" },
  };

  const confirmPinRules = {
    ...pinRules,
    validate: (value) => value === pinForm.getValues("new_pin") || "PINs do not match",
  };

  const pinInputProps = { maxLength: 4, inputMode: "numeric", pattern: "[0-9]*" };
  const mustChangePassword = localStorage.getItem("mustChangePassword") === "1";

  const shareApp = async () => {
    const message = [
      "Try 11Plus Magic — 11+ practice on mobile:",
      `Google Play: ${PLAY_STORE_URL}`,
      APPLE_STORE_URL ? `App Store: ${APPLE_STORE_URL}` : null,
    ]
      .filter(Boolean)
      .join("\n");

    try {
      if (navigator.share) {
        await navigator.share({
          title: "11Plus Magic",
          text: message,
          url: PLAY_STORE_URL,
        });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(message);
        toast.success("App store links copied to clipboard");
      } else {
        setShareOpen(true);
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        setShareOpen(true);
      }
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Typography variant="h5" mb={3}>
        Settings
      </Typography>

      {mustChangePassword && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You must change your password before continuing. This is required on your first login after registration.
        </Alert>
      )}

      {mustChangePin && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Please choose your own parent PIN below. Use the PIN we emailed you as the current PIN —
          it only works until you set a new one.
        </Alert>
      )}

      {paymentReturnMessage && (
        <Alert
          severity={paymentReturnMessage}
          sx={{ mb: 3 }}
          onClose={() => {
            setPaymentReturnMessage(null);
            setPaymentReturnText("");
          }}
        >
          {paymentReturnText}
        </Alert>
      )}

      {!hasMobileChild && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Subscription and Share App are available when at least one of your children uses the 11Plus Magic mobile app.
        </Alert>
      )}

      {/* Mobile-only: Subscription */}
      <Card variant="outlined" sx={{ mb: 3, borderColor: "divider", opacity: hasMobileChild ? 1 : 0.72 }}>
        <CardContent sx={{ pb: "12px !important" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.5 }}>
            <Typography variant="overline" fontWeight={700} color="text.secondary">
              Subscription
            </Typography>
            {hasMobileChild && (
              <Typography variant="caption" color="text.secondary">
                Mobile app · {mobileChildNames || "child account"}
              </Typography>
            )}
          </Box>
          <List disablePadding>
            <SettingRow
              icon={<CardMembershipOutlinedIcon />}
              label="Manage Plan"
              value={managePlanValue}
              disabled={!hasMobileChild}
              onClick={() => hasMobileChild && openSubscriptionDialog("manage")}
            />
            <Divider />
            <SettingRow
              icon={<CreditCardOutlinedIcon />}
              label="Payment options"
              value={hasMobileChild ? "Update card or invoices" : "Mobile app only"}
              disabled={!hasMobileChild}
              onClick={() => hasMobileChild && openSubscriptionDialog("payment")}
            />
            <Divider />
            <SettingRow
              icon={<CancelOutlinedIcon />}
              label="Cancel subscription"
              value={hasMobileChild ? "Turn off auto-renew" : "Mobile app only"}
              disabled={!hasMobileChild}
              onClick={() => hasMobileChild && openSubscriptionDialog("cancel")}
            />
            <Divider />
            <SettingRow
              icon={<HistoryOutlinedIcon />}
              label="Payment History"
              value={hasMobileChild ? "Past and current plans" : "Mobile app only"}
              disabled={!hasMobileChild}
              onClick={() => hasMobileChild && openSubscriptionDialog("history")}
            />
          </List>
        </CardContent>
      </Card>

      {/* Settings (incl. Share App gated) */}
      <Card variant="outlined" sx={{ mb: 3, borderColor: "divider" }}>
        <CardContent sx={{ pb: "12px !important" }}>
          <Typography variant="overline" fontWeight={700} color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            Settings
          </Typography>
          <List disablePadding>
            <SettingRow
              icon={<NotificationsOutlinedIcon />}
              label="Notifications"
              value="Managed in the mobile app"
              onClick={() =>
                toast.info("Open the 11Plus Magic mobile app to manage push notifications.")
              }
            />
            <Divider />
            <SettingRow
              icon={<ShareOutlinedIcon />}
              label="Share App"
              value={hasMobileChild ? "" : "Mobile app only"}
              disabled={!hasMobileChild}
              onClick={() => hasMobileChild && void shareApp()}
            />
            <Divider />
            <SettingRow
              icon={<LockOutlinedIcon />}
              label="Data & Privacy"
              onClick={() => setPrivacyOpen(true)}
            />
            <Divider />
            <SettingRow
              icon={<HelpOutlineIcon />}
              label="Help & Support"
              onClick={() => setHelpOpen(true)}
            />
          </List>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: "100%", borderColor: "divider" }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} mb={2}>
                Update Profile
              </Typography>
              <form onSubmit={profileForm.handleSubmit(onUpdateProfile)}>
                <Grid container spacing={2}>
                  <InputField
                    control={profileForm.control}
                    name="first_name"
                    label="First Name"
                    error={profileForm.formState.errors.first_name}
                    fullWidth
                  />
                  <InputField
                    control={profileForm.control}
                    name="last_name"
                    label="Last Name"
                    error={profileForm.formState.errors.last_name}
                    fullWidth
                  />
                  <SubmitButton loading={loadingProfile} label="Update Profile" />
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: "100%", borderColor: "divider" }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} mb={2}>
                Change Parent Password
              </Typography>
              <form onSubmit={passwordForm.handleSubmit(onChangePassword)}>
                <Grid container spacing={2}>
                  <InputField
                    control={passwordForm.control}
                    name="current_password"
                    label="Current Password"
                    type="password"
                    error={passwordForm.formState.errors.current_password}
                    autoComplete="current-password"
                    fullWidth
                  />
                  <InputField
                    control={passwordForm.control}
                    name="new_password"
                    label="New Password"
                    type="password"
                    error={passwordForm.formState.errors.new_password}
                    autoComplete="new-password"
                    fullWidth
                  />
                  <InputField
                    control={passwordForm.control}
                    name="new_password_confirmation"
                    label="Confirm New Password"
                    type="password"
                    error={passwordForm.formState.errors.new_password_confirmation}
                    autoComplete="new-password"
                    fullWidth
                  />
                  <SubmitButton loading={loadingPassword} label="Change Parent Password" />
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: "100%", borderColor: "divider" }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} mb={0.5}>
                Change PIN
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Use this PIN to switch from student portal to parent portal.
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1.5 }}>
                PIN validation: exactly 4 digits (0–9 only).
              </Typography>
              <form onSubmit={pinForm.handleSubmit(onChangePin)}>
                <Grid container spacing={2}>
                  <InputField
                    control={pinForm.control}
                    name="current_pin"
                    label="Current PIN"
                    type="password"
                    error={pinForm.formState.errors.current_pin}
                    autoComplete="off"
                    fullWidth
                    rules={pinRules}
                    inputProps={pinInputProps}
                  />
                  <InputField
                    control={pinForm.control}
                    name="new_pin"
                    label="New PIN"
                    type="password"
                    error={pinForm.formState.errors.new_pin}
                    autoComplete="off"
                    fullWidth
                    rules={pinRules}
                    inputProps={pinInputProps}
                  />
                  <InputField
                    control={pinForm.control}
                    name="new_pin_confirmation"
                    label="Confirm New PIN"
                    type="password"
                    error={pinForm.formState.errors.new_pin_confirmation}
                    autoComplete="off"
                    fullWidth
                    rules={confirmPinRules}
                    inputProps={pinInputProps}
                  />
                  <SubmitButton loading={loadingPin} label="Change PIN" />
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Button
        fullWidth
        variant="outlined"
        color="error"
        startIcon={<DeleteOutlineIcon />}
        sx={{ mt: 3, py: 1.25, borderRadius: 2, bgcolor: "rgba(244,63,94,0.06)" }}
        onClick={() => setDeleteOpen(true)}
      >
        Delete Account
      </Button>

      <Dialog open={privacyOpen} onClose={() => setPrivacyOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Data & Privacy</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            We use your account details to provide learning progress, billing, and parental controls. You can review
            our full policy any time.
          </Typography>
          <Link href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
            Open privacy policy
          </Link>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrivacyOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Help & Support</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            Need help with the parent portal or mobile app? Email us and include your child&apos;s name and the issue.
          </Typography>
          <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={shareOpen} onClose={() => setShareOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Share App</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            Share these store links with family or friends:
          </Typography>
          <Typography variant="body2">
            <Link href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
              Google Play
            </Link>
          </Typography>
          {APPLE_STORE_URL ? (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <Link href={APPLE_STORE_URL} target="_blank" rel="noopener noreferrer">
                App Store
              </Link>
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={subscriptionOpen} onClose={() => setSubscriptionOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {subscriptionMode === "history"
            ? "Payment History"
            : subscriptionMode === "cancel"
              ? "Cancel subscription"
              : subscriptionMode === "payment"
                ? "Payment options"
                : "Manage Plan"}
        </DialogTitle>
        <DialogContent>
          {subscriptionMode === "history" ? (
            <>
              <Typography variant="body2" color="text.secondary" paragraph>
                Past and current subscriptions
              </Typography>
              {loadingHistory ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : historyError ? (
                <Alert severity="error">{historyError}</Alert>
              ) : paymentHistoryChildren.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No payment history found.
                </Typography>
              ) : (
                <Stack spacing={2.5} sx={{ mt: 0.5 }}>
                  {paymentHistoryChildren.map((child) => {
                    const history = Array.isArray(child.history) ? child.history : [];
                    const current = history.find((item) =>
                      ["active", "trial"].includes(String(item.status || "").toLowerCase())
                    );
                    const childId = Number(child.student_user_id || child.user_id || child.mobile_user_id || 0);
                    const busyPortal = portalBusyId === childId;

                    return (
                      <Box key={childId || child.child_name}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Typography variant="subtitle1" fontWeight={600}>
                            {child.child_name || "Child"}
                          </Typography>
                          <Button
                            size="small"
                            variant="text"
                            disabled={busyPortal}
                            onClick={() => void openBillingPortal(child)}
                          >
                            {busyPortal ? <CircularProgress size={16} /> : "Stripe invoices"}
                          </Button>
                        </Box>
                        {current?.current_period_end ? (
                          <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
                            Current plan expires {formatPlanExpiryDate(current.current_period_end)}
                          </Alert>
                        ) : null}
                        {!history.length ? (
                          <Typography variant="body2" color="text.secondary">
                            No payment history found.
                          </Typography>
                        ) : (
                          <Stack spacing={1.25}>
                            {history.map((item) => {
                              const isPayment = item.row_type === "payment";
                              return (
                              <Box
                                key={`${item.row_type || "subscription"}-${item.id}`}
                                sx={{
                                  p: 1.5,
                                  border: "1px solid",
                                  borderColor: "divider",
                                  borderRadius: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                    gap: 1,
                                    mb: 0.75,
                                  }}
                                >
                                  <Box>
                                    <Typography variant="subtitle2">
                                      {item.plan?.name || "Unknown Plan"}
                                    </Typography>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5 }}>
                                      <Typography variant="caption" color="text.secondary">
                                        Status:
                                      </Typography>
                                      <Chip
                                        size="small"
                                        label={statusLabel(item.status)}
                                        color={statusChipColor(item.status)}
                                        variant="outlined"
                                      />
                                    </Box>
                                  </Box>
                                  <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: "nowrap" }}>
                                    {isPayment
                                      ? formatPlanPrice({
                                          price_pence: item.amount_pence,
                                          currency: item.currency,
                                        })
                                      : formatPlanPrice(item.plan)}
                                    {!isPayment ? (
                                      <Typography component="span" variant="caption" color="text.secondary">
                                        /{item.plan?.billing_interval || "month"}
                                      </Typography>
                                    ) : null}
                                  </Typography>
                                </Box>
                                {isPayment ? (
                                  <>
                                    <Typography variant="body2" color="text.secondary">
                                      Date: {formatUkDateTime(item.paid_at || item.created_at)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Platform: {item.payment_platform || item.payment_method || "Unknown"}
                                      {item.is_automatic ? " · Automatic charge" : ""}
                                    </Typography>
                                  </>
                                ) : (
                                  <>
                                    <Typography variant="body2" color="text.secondary">
                                      Start: {formatUkDateTime(item.current_period_start)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Expires: {formatPlanExpiryDate(item.current_period_end)}
                                    </Typography>
                                  </>
                                )}
                                {item.auto_charge_due_at ? (
                                  <Typography variant="body2" color="text.secondary">
                                    Auto charge due: {formatUkDateTime(item.auto_charge_due_at)}
                                  </Typography>
                                ) : null}
                              </Box>
                              );
                            })}
                          </Stack>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </>
          ) : subscriptionMode === "cancel" ? (
            <>
              <Typography variant="body2" paragraph>
                Turning off auto-renew keeps access until the end of the current billing period. You can
                subscribe again later from Manage Plan.
              </Typography>
              {loadingSubscriptions && displayChildren.length === 0 ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <Stack spacing={1.5} sx={{ mt: 1 }}>
                  {displayChildren.map((child) => {
                    const id = Number(child.student_user_id || child.user_id || child.mobile_user_id || 0);
                    const busy = unsubscribeBusyId === id;
                    const external = isExternalBilling(child);
                    const canCancel = Boolean(child.can_unsubscribe) && !external;
                    const canResume = Boolean(child.can_resume) && !external;
                    const busyResume = resumeBusyId === id;

                    return (
                      <Box
                        key={id || child.child_name}
                        sx={{
                          p: 1.5,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="subtitle2">{child.child_name || "Child"}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {child.subscription_display_name || "Mobile"}
                          {child.current_period_end
                            ? ` · access until ${formatPlanExpiryDate(child.current_period_end)}`
                            : ""}
                        </Typography>
                        {child.unsubscribed_at ? (
                          <>
                            <Alert severity="info" sx={{ py: 0.5 }}>
                              Auto-renew is off.
                              {child.current_period_end
                                ? ` You keep access until ${formatPlanExpiryDate(child.current_period_end)}.`
                                : ""}
                            </Alert>
                            {/* Cancelling only set cancel_at_period_end in Stripe, so this is a
                                straight undo while the paid window is still open. Without it a
                                parent could only get auto-renew back by upgrading to a dearer
                                plan — and not at all on the top plan. */}
                            {canResume ? (
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={busyResume}
                                onClick={() => void resumeSubscription(child)}
                                sx={{ mt: 1.5 }}
                              >
                                {busyResume ? (
                                  <CircularProgress size={18} color="inherit" />
                                ) : (
                                  "Turn auto-renew back on"
                                )}
                              </Button>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                This plan has ended. Subscribe again from Manage Plan.
                              </Typography>
                            )}
                          </>
                        ) : external ? (
                          <Alert severity="info" sx={{ py: 0.5 }}>
                            Manage cancellation in the App Store or Google Play.
                          </Alert>
                        ) : canCancel ? (
                          <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Please tell us why you are cancelling. This is required before we turn
                              off auto-renew.
                            </Typography>
                            <RadioGroup
                              value={unsubscribeChoiceByChild[id] || ""}
                              onChange={(e) =>
                                setUnsubscribeChoiceByChild((prev) => ({ ...prev, [id]: e.target.value }))
                              }
                            >
                              <Stack spacing={1}>
                                {[
                                  ...cancelReasons.map((r) => ({ value: String(r.id), label: r.name })),
                                  { value: OTHER_REASON, label: OTHER_REASON },
                                ].map((option) => {
                                  const selected = (unsubscribeChoiceByChild[id] || "") === option.value;
                                  return (
                                    <FormControlLabel
                                      key={option.value}
                                      value={option.value}
                                      disabled={busy}
                                      control={<Radio size="small" />}
                                      label={<Typography variant="body2">{option.label}</Typography>}
                                      sx={{
                                        m: 0,
                                        px: 1,
                                        py: 0.5,
                                        border: "1px solid",
                                        borderColor: selected ? "primary.main" : "divider",
                                        borderRadius: 1,
                                        bgcolor: selected ? "action.hover" : "transparent",
                                      }}
                                    />
                                  );
                                })}
                              </Stack>
                            </RadioGroup>

                            {/* Only "Other" needs typing — a preset is already the reason. */}
                            {(unsubscribeChoiceByChild[id] || "") === OTHER_REASON && (
                              <TextField
                                fullWidth
                                size="small"
                                multiline
                                minRows={2}
                                autoFocus
                                label="Reason for cancelling"
                                placeholder="Tell us why you are turning off auto-renew"
                                value={unsubscribeReasonByChild[id] || ""}
                                onChange={(e) =>
                                  setUnsubscribeReasonByChild((prev) => ({ ...prev, [id]: e.target.value }))
                                }
                                inputProps={{ maxLength: 1000 }}
                                disabled={busy}
                                sx={{ mt: 1.5 }}
                              />
                            )}

                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              disabled={busy || resolvedCancelReason(id).text.length < 3}
                              onClick={() => void cancelSubscription(child)}
                              sx={{ mt: 1.5 }}
                            >
                              {busy ? <CircularProgress size={18} color="inherit" /> : "Turn off auto-renew"}
                            </Button>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No cancellable subscription for this child.
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </>
          ) : subscriptionMode === "payment" ? (
            <>
              <Typography variant="body2" paragraph>
                Update the saved card or view Stripe invoices for each mobile child account.
              </Typography>
              {loadingSubscriptions && displayChildren.length === 0 ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <Stack spacing={1.5} sx={{ mt: 1 }}>
                  {displayChildren.map((child) => {
                    const id = Number(child.student_user_id || child.user_id || child.mobile_user_id || 0);
                    const busyPortal = portalBusyId === id;
                    const external = isExternalBilling(child);

                    return (
                      <Box
                        key={id || child.child_name}
                        sx={{
                          p: 1.5,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="subtitle2">{child.child_name || "Child"}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {child.subscription_display_name || "Mobile"}
                        </Typography>
                        {external ? (
                          <Alert severity="info" sx={{ py: 0.5 }}>
                            Payment method is managed in the App Store or Google Play.
                          </Alert>
                        ) : (
                          <Button
                            size="small"
                            variant="contained"
                            endIcon={busyPortal ? null : <ArrowForwardIcon />}
                            disabled={busyPortal}
                            onClick={() => void openBillingPortal(child)}
                          >
                            {busyPortal ? (
                              <CircularProgress size={18} color="inherit" />
                            ) : (
                              "Update payment method"
                            )}
                          </Button>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </>
          ) : (
            <>
              <Typography variant="body2" paragraph>
                Upgrade a child to a higher plan, or start a subscription when there isn&apos;t one. Moving to a
                cheaper plan is not done here — turn off auto-renew under Cancel subscription first.
              </Typography>
              {loadingSubscriptions && displayChildren.length === 0 ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <Stack spacing={1.5} sx={{ mt: 1 }}>
                  {displayChildren.map((child) => {
                    const id = Number(child.student_user_id || child.user_id || child.mobile_user_id || 0);
                    const needsPay = childNeedsPayment(child);
                    const busyCheckout = checkoutBusyId === id;
                    const busyPortal = portalBusyId === id;
                    const external = isExternalBilling(child);
                    const paidPlans = plansAvailable.filter((p) => Number(p.price_pence || 0) > 0);
                    const allChoices = paidPlans.length ? paidPlans : plansAvailable;
                    const coursePlans = Array.isArray(child.course_plans) ? child.course_plans : [];
                    const canSwapInPlace =
                      !needsPay &&
                      !external &&
                      Number(child.subscription_id || 0) > 0 &&
                      String(child.payment_provider || "stripe").toLowerCase() === "stripe";
                    const currentPlanPrice = Number(child.effective_current_plan?.price_pence || 0);
                    // Plan changes only move up the price ladder; a cheaper plan means cancelling first.
                    const planChoices = canSwapInPlace
                      ? allChoices.filter((p) => Number(p.price_pence || 0) > currentPlanPrice)
                      : allChoices;
                    const noUpgradeAvailable = canSwapInPlace && planChoices.length === 0;
                    const chosenPlanId = selectedPlanByChild[id];
                    const selectedPlan = planChoices.some((p) => Number(p.id) === Number(chosenPlanId))
                      ? chosenPlanId
                      : planChoices[0]?.id || "";

                    return (
                      <Box
                        key={id || child.child_name}
                        sx={{
                          p: 1.5,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="subtitle2">{child.child_name || "Child"}</Typography>
                        <Typography
                          variant="overline"
                          color="text.secondary"
                          sx={{ display: "block", mt: 0.5, lineHeight: 1.6 }}
                        >
                          Mobile app plan
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
                          <Typography variant="body2" color="text.secondary">
                            {child.subscription_display_name || "Mobile"}
                          </Typography>
                          <Chip
                            size="small"
                            label={planStateLabel(child.plan_state, child.subscription_status)}
                            color={planStateChipColor(child.plan_state, child.subscription_status)}
                            variant="outlined"
                          />
                          {/* "renews" was printed unconditionally, so a lapsed plan read
                              "Active - renews <a past date>" while checkout said it had ended. */}
                          {child.current_period_end && planStateDateLabel(child.plan_state) ? (
                            <Typography variant="caption" color="text.secondary">
                              {planStateDateLabel(child.plan_state)}{" "}
                              {formatPlanExpiryDate(child.current_period_end)}
                            </Typography>
                          ) : null}
                        </Box>
                        {/* The upcoming charge, visible before it lands on the statement. */}
                        {child.pending_plan && !child.unsubscribed_at ? (
                          <Alert severity="info" sx={{ py: 0.5, mb: 1.25 }}>
                            Switching to {child.pending_plan.name} on{" "}
                            {formatPlanExpiryDate(child.pending_plan.starts_at || child.current_period_end)}.{" "}
                            {formatPlanPrice(child.pending_plan)}/{child.pending_plan.billing_interval || "month"} will
                            be charged then. To stop it, turn off auto-renew under Cancel subscription.
                          </Alert>
                        ) : null}
                        {external ? (
                          <Alert severity="info" sx={{ py: 0.5 }}>
                            This plan is managed in the App Store or Google Play.
                          </Alert>
                        ) : (
                          <>
                            {planChoices.length > 0 ? (
                              <TextField
                                select
                                fullWidth
                                size="small"
                                label={canSwapInPlace ? "Upgrade to" : "Plan"}
                                value={selectedPlan}
                                onChange={(e) =>
                                  setSelectedPlanByChild((prev) => ({
                                    ...prev,
                                    [id]: e.target.value,
                                  }))
                                }
                                sx={{ mb: 1.25 }}
                              >
                                {planChoices.map((plan) => (
                                  <MenuItem key={plan.id} value={plan.id}>
                                    {plan.name} · {formatPlanPrice(plan)}/{plan.billing_interval || "month"}
                                  </MenuItem>
                                ))}
                              </TextField>
                            ) : null}
                            {noUpgradeAvailable ? (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                This is the highest plan. To move to a cheaper plan, turn off auto-renew in
                                Cancel subscription and subscribe again when this plan ends.
                              </Typography>
                            ) : null}
                            {needsPay || planChoices.length > 0 ? (
                              <Button
                                size="small"
                                variant="contained"
                                endIcon={busyCheckout ? null : <ArrowForwardIcon />}
                                disabled={busyCheckout || (canSwapInPlace && !selectedPlan)}
                                onClick={() =>
                                  void (canSwapInPlace
                                    ? changePlanInPlace(child, selectedPlan)
                                    : startCheckout(child, selectedPlan))
                                }
                              >
                                {busyCheckout ? (
                                  <CircularProgress size={18} color="inherit" />
                                ) : needsPay ? (
                                  "Continue to payment"
                                ) : (
                                  "Upgrade plan"
                                )}
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                variant="contained"
                                endIcon={busyPortal ? null : <ArrowForwardIcon />}
                                disabled={busyPortal}
                                onClick={() => void openBillingPortal(child)}
                              >
                                {busyPortal ? <CircularProgress size={18} color="inherit" /> : "Manage billing"}
                              </Button>
                            )}
                            {canSwapInPlace && planChoices.length > 0 ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mt: 0.75 }}
                              >
                                The upgrade is charged pro rata to the saved card and keeps the same renewal
                                date. Only higher plans are offered here.
                              </Typography>
                            ) : null}
                          </>
                        )}
                        <Typography
                          variant="overline"
                          color="text.secondary"
                          sx={{ display: "block", mt: 1.5, lineHeight: 1.6 }}
                        >
                          Course plan
                        </Typography>
                        {coursePlans.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No course plan for this child.
                          </Typography>
                        ) : (
                          <Stack spacing={0.5}>
                            {coursePlans.map((row) => {
                              const label = formatCoursePlanLabel(row);
                              return (
                                <Box key={`${row.course_id}_${row.course_plan_id || 0}`}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                    <Typography variant="body2" color="text.secondary">
                                      {label.title}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={statusLabel(row.status)}
                                      color={statusChipColor(row.status)}
                                      variant="outlined"
                                    />
                                  </Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {[label.where, row.start_date && row.end_date
                                      ? `${formatPlanExpiryDate(row.start_date)} – ${formatPlanExpiryDate(row.end_date)}`
                                      : null]
                                      .filter(Boolean)
                                      .join(" · ") || "Billed with your course payments"}
                                  </Typography>
                                </Box>
                              );
                            })}
                            <Typography variant="caption" color="text.secondary">
                              Course plans are billed separately — manage them under Billing.
                            </Typography>
                          </Stack>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubscriptionOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            Deleting a parent or mobile student account is permanent. To request deletion, contact support with the
            account email and child name. Mobile app users can also delete from Profile → Delete Account in the app
            (parental approval required).
          </Typography>
          <Link href={`mailto:${SUPPORT_EMAIL}?subject=Delete%20account%20request`}>
            Email {SUPPORT_EMAIL}
          </Link>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Setting;
